import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Sparkles } from "lucide-react";
import { api, money0 } from "../../api";
import { useAuth } from "../../auth";
import { Kpi } from "../bits";
import { Thinking, openAsk } from "./AskTraide";

const TTL = 10 * 60 * 1000;
const key = (tid) => `traide-brief-${tid || "x"}`;

export function readBrief(tid) {
  try { const c = JSON.parse(sessionStorage.getItem(key(tid)) || "null"); return c && Date.now() - c.at < TTL ? c.data : null; } catch { return null; }
}

export async function loadBrief(tid, force) {
  if (!force) { const c = readBrief(tid); if (c) return c; }
  const r = await api.get("/intelligence/brief");
  try { sessionStorage.setItem(key(tid), JSON.stringify({ at: Date.now(), data: r.data })); } catch { /* storage full or disabled */ }
  return r.data;
}

export const briefName = (b) => /morning/i.test(b?.greeting || "") ? "Morning brief" : /afternoon/i.test(b?.greeting || "") ? "Afternoon brief" : "Evening brief";

export function useBrief() {
  const { user } = useAuth();
  const tid = user?.tenant_id;
  const [b, setB] = useState(() => readBrief(tid));
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = (force) => { setBusy(true); setErr(false); loadBrief(tid, force).then(setB).catch(() => setErr(true)).finally(() => setBusy(false)); };
  useEffect(() => { if (!b) load(false); }, [tid]); // eslint-disable-line react-hooks/exhaustive-deps
  return { b, err, busy, reload: () => load(true) };
}

// Home: the one thing to deal with first, in a sentence. Nothing else.
export function BriefCard({ fallback }) {
  const { b, err } = useBrief();
  if (err) return fallback ? <p className="t3 flex items-start gap-2 px-1 text-[12.5px]" data-testid="home-insight"><Sparkles size={13} className="mt-0.5 shrink-0 text-amber-500" /><span><span className="t2 font-semibold">TRAIDE insight</span> · {fallback}</span></p> : null;
  if (!b) return <div className="card flex items-center gap-3 p-4" data-testid="home-brief-loading"><span className="intel-mark"><Sparkles size={14} /></span><Thinking label="TRAIDE is preparing your brief" /></div>;
  return (
    <section className="card p-4" data-testid="home-brief">
      <div className="flex items-start gap-3">
        <span className="intel-mark mt-0.5"><Sparkles size={14} /></span>
        <div className="min-w-0 flex-1">
          <div className="eyebrow">TRAIDE Intelligence · {briefName(b)}</div>
          <p className="t1 mt-1 text-[14px] leading-relaxed" data-testid="home-brief-priority">{b.priority}</p>
          {b.second && <p className="t2 mt-1 text-[13px] leading-relaxed" data-testid="home-brief-second">{b.second}</p>}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Link to={b.priority_link} className="btn-primary btn-sm" data-testid="home-brief-action">{b.priority_action}</Link>
            <Link to="/intelligence" className="btn-secondary btn-sm" data-testid="home-brief-open">Open Intelligence</Link>
            <button type="button" className="btn-secondary btn-sm" onClick={() => openAsk({})} data-testid="home-brief-ask"><Sparkles size={12} /> Ask TRAIDE</button>
          </div>
        </div>
      </div>
    </section>
  );
}

// Intelligence Center: the full brief — sentence first, then the numbers it was built from.
export function BriefPanel() {
  const { b, err, busy, reload } = useBrief();
  if (err) return <div className="card flex items-center justify-between gap-3 p-4 text-[13px]" data-testid="intel-brief-error"><span className="t1">Couldn't build the brief.</span><button className="btn-secondary btn-sm" onClick={reload}>Retry</button></div>;
  if (!b) return <div className="card flex items-center gap-3 p-4" data-testid="intel-brief-loading"><span className="intel-mark"><Sparkles size={14} /></span><Thinking label="TRAIDE is preparing your brief" /></div>;
  const t = b.today, biz = b.business;
  return (
    <section className="card overflow-hidden" data-testid="intel-brief">
      <div className="flex items-start gap-3 p-4">
        <span className="intel-mark mt-0.5"><Sparkles size={14} /></span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3"><div className="eyebrow">{briefName(b)} · {b.greeting}, {b.name}</div><button type="button" className="btn-icon !h-7 !w-7" onClick={reload} disabled={busy} aria-label="Refresh brief" data-testid="intel-brief-refresh"><RefreshCw size={13} className={busy ? "animate-spin" : ""} /></button></div>
          <p className="t1 mt-1 text-[15px] leading-relaxed" data-testid="intel-brief-priority">{b.priority}</p>
          {b.second && <p className="t2 mt-1 text-[13.5px] leading-relaxed" data-testid="intel-brief-second">{b.second}</p>}
          <div className="mt-3"><Link to={b.priority_link} className="btn-primary btn-sm" data-testid="intel-brief-action">{b.priority_action}</Link></div>
        </div>
      </div>
      <div className="strip grid-cols-2 !rounded-none !border-x-0 !border-b-0 sm:grid-cols-4 lg:grid-cols-8" data-testid="intel-brief-numbers">
        <Kpi label="Revenue this month" value={money0(biz.revenue_month)} sub={`~${money0(biz.expected_gross_profit)} gross profit`} testid="brief-revenue" />
        <Kpi label="Pipeline" value={money0(biz.pipeline_value)} sub={`${biz.open_leads} open leads`} testid="brief-pipeline" to="/deals/pipeline" />
        <Kpi label="Quotes awaiting" value={t.quotes_awaiting} sub={money0(t.quotes_awaiting_value)} testid="brief-quotes" to="/deals/quotes?status=sent" />
        <Kpi label="Overdue invoices" value={t.invoices_overdue} sub={money0(t.overdue_value)} tone={t.overdue_value > 0 ? "rose" : undefined} testid="brief-overdue" to="/money/invoices?status=overdue" />
        <Kpi label="Jobs underway" value={t.jobs_underway} sub={`${t.jobs_starting} starting today`} testid="brief-jobs" to="/jobs" />
        <Kpi label="Team live" value={t.team_live} sub={t.staff_exceptions ? `${t.staff_exceptions} not clocked in` : "everyone accounted for"} tone={t.staff_exceptions ? "amber" : undefined} testid="brief-team" to="/dispatch/live" />
        <Kpi label="Deliveries today" value={t.deliveries_today} sub={t.deliveries_delayed ? `${t.deliveries_delayed} delayed` : "none delayed"} tone={t.deliveries_delayed ? "amber" : undefined} testid="brief-deliveries" to="/supply/deliveries" />
        <Kpi label="POs to approve" value={t.po_awaiting} sub={t.high_priority_tasks ? `${t.high_priority_tasks} urgent tasks` : "orders wait for you"} tone={t.po_awaiting ? "amber" : undefined} testid="brief-po" to="/supply/purchase-orders?status=awaiting_approval" />
      </div>
    </section>
  );
}
